import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {t} from 'i18next';
import './CreateNewIndex.scss';
import {ISelectItem} from '../../../../../../shared/types';
import {
  ArrTreeItem,
  buildIdMap,
  filterTree,
  labelsAndCountFromSelection,
  MAX_INDEX_NAME,
  normalizeEmail,
  normalizeNodes,
  RawTreeNode,
  validateEmail,
  validateIndexName,
  validateSourceIds,
} from '../../../helpers/aihelper';
import {ArrDrawer} from '../../../../../common/arr-drawer/ArrDrawer';
import {Box, IconButton, InputAdornment, Typography} from '@mui/material';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import ArrTextField from '../../../../../common/arr-text-field/ArrTextField';
import ArrSingleSelectDropdown from '../../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {Search} from '@mui/icons-material';
import {ReactComponent as InfoIcon} from '../../../../../../assets/images/info-icon.svg';
import ArrSelectionChip from '../../../../../common/arr-selection-chip/ArrSelectionChip';
import ArrTreeView from '../../../../../common/arr-tree-view/ArrTreeView';
import ArrModal from '../../../../../common/arr-modal/ArrModal';
import ConfirmModal from '../../confirm-modal/ConfirmModal';
import ArrTooltipWrapper from '../../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {
  useGetFoldersList,
  useGetTagsList,
} from '../../../../../../hooks/tagsFoldering/useTagsFoldering';
import {ITagsList} from '../../../../../../api/types/tagFoldering.type';
import {RootState} from '../../../../../../redux/store';
import {useSelector} from 'react-redux';
import ArrLoadingSpinner from '../../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {
  useCreateInvestigateIndex,
  useGetAIInvestigateIndexSourceDocumentCount,
  useGetIndicesList,
  useGetSavedSearchList,
} from '../../../../../../hooks/ai/investigate/useInvestigate';
import {
  IAIInvestigateIndicesPayload,
  IAIInvestigateIndexSourceTypeCode,
  ISavedSearch,
} from '../../../../../../api/types/aiInvestigate.type';
import {ToastType} from '../../../../../../shared/constants';
import {
  TranscriptSortColumn,
  TranscriptSortDirection,
} from '../../../AiInvestigateUtils';
import {Trans} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

type Props = {
  openPanel: boolean;
  onClose: () => void;
  onCreated?: () => void;
  showToast: (message: string, status: ToastType) => void;
};

enum DropdownItemsType {
  savedSearch = 'SAVEDSEARCH',
  tags = 'TAG',
  folders = 'FOLDER',
}

const CreateNewIndex: React.FC<Props> = ({
  openPanel,
  onClose,
  onCreated,
  showToast,
}) => {
  const userSecurityGroupId = useSelector(
    (state: RootState) => state.authenticatedUser.user.securityGroupId
  );
  const [indexName, setIndexName] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');

  const [treeSelectedIds, setTreeSelectedIds] = useState<string[]>([]);

  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emails, setEmails] = useState<string[]>([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [isUnsavedModalOpen, setIsUnsavedModalOpen] = useState<boolean>(false);

  const [touched, setTouched] = useState({
    indexName: false,
    dataset: false,
    tree: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const [selectedDataset, setSelectedDataset] = useState<ISelectItem | null>(
    null
  );
  const [docCount, setDocCount] = useState<number>(0);

  const navigate = useNavigate();

  const indicesPayload: IAIInvestigateIndicesPayload = useMemo(
    () => ({
      sortingColumn: TranscriptSortColumn.CreatedOn,
      direction: TranscriptSortDirection.DESC,
      columnFilter: [
        {
          columnName: 'indexName',
          value: indexName,
        },
      ],
    }),
    [indexName]
  );

  const {refetch: refetchIndicesList, isFetching} = useGetIndicesList(
    indicesPayload,
    {enabled: false}
  );

  const {
    data: foldersList = [],
    refetch: refetchFoldersList,
    dataUpdatedAt: foldersListUpdatedAt,
    isFetching: isLoadingFoldersList,
  } = useGetFoldersList(
    `?securityGroupId=${userSecurityGroupId}&toggelValue=0`,
    false
  );

  const {
    data: tagsData,
    refetch: refetchTagsList,
    dataUpdatedAt: tagsListUpdatedAt,
    isFetching: isLoadingTagsList,
  } = useGetTagsList(
    `?securityGroupId=${userSecurityGroupId}&toggelValue=0`,
    false
  );

  const {
    mutate: fetchSavedSearchList,
    data: savedSearchList = [],
    isPending: isLoadingSavedSearchList,
  } = useGetSavedSearchList();

  const {
    mutate: fetchSourceDocumentCount,
    isPending: isApiDocumentCountPending,
  } = useGetAIInvestigateIndexSourceDocumentCount(
    (data) => {
      setDocCount(data.documentCount);
    },
    (error) => {
      setDocCount(0);
    }
  );

  const {
    mutate: createIndex,
    error: createError,
    isPending,
  } = useCreateInvestigateIndex((data) => {
    showToast(
      t('ai.investigate.createEditIndex.create.successToastMessage'),
      ToastType.Success
    );
    setIsConfirmModalOpen(false);
    const createdIndexData = data;
    navigate('/AI/Investigate/manage-indices/details', {
      state: {
        active: createdIndexData.status,
        indexId: createdIndexData.indexId,
        indexName: createdIndexData.indexName,
      },
    });
    onClose();
    onCreated?.();
  });

  const isTreeLoading = useMemo(
    () => isLoadingFoldersList || isLoadingTagsList || isLoadingSavedSearchList,
    [isLoadingFoldersList, isLoadingTagsList, isLoadingSavedSearchList]
  );

  useEffect(() => {
    if (selectedDataset?.value === DropdownItemsType.folders) {
      refetchFoldersList();
    } else if (selectedDataset?.value === DropdownItemsType.tags) {
      refetchTagsList();
    } else if (selectedDataset?.value === DropdownItemsType.savedSearch) {
      fetchSavedSearchList(false);
    }
  }, [selectedDataset]);

  const structuredTreeData = useMemo((): RawTreeNode[] => {
    if (
      !foldersList.length &&
      !tagsData?.tagList.length &&
      !savedSearchList.length
    )
      return [];
    const treeItems = [];

    if (selectedDataset?.value === DropdownItemsType.folders) {
      treeItems.push(...foldersList);
    } else if (selectedDataset?.value === DropdownItemsType.tags) {
      treeItems.push(...(tagsData?.tagList || []));
    } else if (selectedDataset?.value === DropdownItemsType.savedSearch) {
      treeItems.push(...savedSearchList);
    }
    const nodeMap = new Map<string, RawTreeNode>();

    treeItems.forEach((item: ITagsList | ISavedSearch) => {
      nodeMap.set(item.id, {
        id: item.id,
        label: item.textWithoutCount || item.text,
        docCount: item.documentCount,
        children: [],
      });
    });

    const roots: RawTreeNode[] = [];
    treeItems.forEach((item: ITagsList | ISavedSearch) => {
      const node = nodeMap.get(item.id);
      if (!node) return;

      const parent = nodeMap.get(item.parent);
      if (!parent) {
        roots.push(node);
      } else {
        parent.children = [...(parent.children || []), node];
      }
    });

    return roots;
  }, [
    foldersList,
    foldersListUpdatedAt,
    tagsData,
    tagsListUpdatedAt,
    savedSearchList,
    selectedDataset,
  ]);

  const normalizedData: ArrTreeItem[] = useMemo(() => {
    if (structuredTreeData.length > 0) {
      return normalizeNodes(structuredTreeData);
    }
    return [];
  }, [structuredTreeData]);

  const canUseTree = selectedDataset !== null;

  const indexNameErr = touched.indexName ? validateIndexName(indexName) : null;
  const docsErr = touched.tree ? validateSourceIds(treeSelectedIds) : null;

  const formValid =
    !validateIndexName(indexName) && !validateSourceIds(treeSelectedIds);

  const displayDocCount =
    treeSelectedIds.length === 0 ? '--' : docCount.toLocaleString('en-US');
  const lastDocCountRequestKeyRef = useRef<string>('');

  useEffect(() => {
    if (treeSelectedIds.length > 0) {
      const sourceTypeCode =
        selectedDataset?.value as IAIInvestigateIndexSourceTypeCode;
      const requestKey = `${sourceTypeCode}:${treeSelectedIds.join(',')}`;

      if (lastDocCountRequestKeyRef.current === requestKey) return;
      lastDocCountRequestKeyRef.current = requestKey;

      fetchSourceDocumentCount({
        sourceTypeCode,
        sourceIds: treeSelectedIds,
      });
    } else {
      lastDocCountRequestKeyRef.current = '';
      setDocCount(0);
    }
  }, [selectedDataset, treeSelectedIds]);

  useEffect(() => {
    if (!openPanel) {
      setIndexName('');
      setSelectedDataset(null);
      setSearchQuery('');
      setAppliedQuery('');
      setTreeSelectedIds([]);
      setEmails([]);
      setEmailInput('');
      setEmailError(null);
      setTouched({indexName: false, dataset: false, tree: false});
      setSubmitting(false);
    }
  }, [openPanel]);

  const handleDatasetChange = (item: ISelectItem | null) => {
    setSelectedDataset(item);
    setTreeSelectedIds([]);
    setSearchQuery('');
    setAppliedQuery('');
    if (!touched.dataset) setTouched((p) => ({...p, dataset: true}));
  };

  const idMap = useMemo(() => buildIdMap(normalizedData), [normalizedData]);

  const handleTreeSelected = useCallback(
    (value: string[] | null) => {
      if (!canUseTree) return;

      const ids = Array.isArray(value) ? value : [];

      const leafOnlyIds = ids.filter((id) => {
        const node = idMap.get(id);
        return !!(node && (!node.children || node.children.length === 0));
      });

      setTreeSelectedIds(leafOnlyIds);

      if (!touched.tree) {
        setTouched((prev) => ({...prev, tree: true}));
      }
    },
    [canUseTree, idMap, touched.tree, setTouched]
  );

  const treeItems: ArrTreeItem[] = useMemo(() => {
    if (!normalizedData.length) return [];
    if (!appliedQuery) return normalizedData;
    return filterTree(normalizedData, appliedQuery);
  }, [normalizedData, appliedQuery]);

  const selectedLabelsDisplay = useMemo(() => {
    if (!normalizedData.length || treeSelectedIds.length === 0) {
      return '--';
    }
    const {labels} = labelsAndCountFromSelection(treeSelectedIds, idMap);
    return labels.length > 0 ? labels.join(', ') : '--';
  }, [normalizedData, treeSelectedIds, idMap]);

  const addEmail = () => {
    const err = validateEmail(emailInput);
    if (err) return setEmailError(err);
    const v = normalizeEmail(emailInput);
    setEmails((p) => [...p.filter((e) => e !== v), v]);
    setEmailInput('');
    setEmailError(null);
  };

  const handleSubmit = async () => {
    setTouched({indexName: true, dataset: true, tree: true});
    if (!formValid) return;

    try {
      const getIndices = await refetchIndicesList();
      const existingIndices = getIndices?.data?.indices || [];

      const isDuplicateIndex = existingIndices.some(
        (item) =>
          item.indexName?.toLowerCase().trim() ===
          indexName.toLowerCase().trim()
      );

      if (isDuplicateIndex) {
        showToast(
          t('ai.investigate.createEditIndex.create.duplicateIndexName'),
          ToastType.Error
        );
        return;
      }

      setIsConfirmModalOpen(true);
    } catch (err) {
      showToast(
        t('ai.investigate.createEditIndex.create.indexNameValidationFailed'),
        ToastType.Error
      );
    }
  };

  const dropdownItems: ISelectItem[] = useMemo(
    () => [
      {
        id: DropdownItemsType.savedSearch,
        value: DropdownItemsType.savedSearch,
        label: t('common.savedSearch'),
      },
      {
        id: DropdownItemsType.tags,
        value: DropdownItemsType.tags,
        label: t('common.tags'),
      },
      {
        id: DropdownItemsType.folders,
        value: DropdownItemsType.folders,
        label: t('common.folders'),
      },
    ],
    []
  );

  return (
    <ArrDrawer
      isOpen={openPanel}
      onClose={onClose}
      showButton={false}
      showHeader={false}
      title={t('ai.investigate.createEditIndex.create.createIndexTitle')}
      size="small"
      side="right"
      data-testid="createindex-drawer"
    >
      <Box className="create-edit-Index">
        <Box className="drawer-container">
          <Box className="drawer-header-box">
            <Box className="drawer-row">
              <Typography
                component="h3"
                role="header"
                aria-label={t(
                  'ai.investigate.createEditIndex.create.createIndexTitle'
                )}
                className="typo-text headline3_size"
                data-testid="create-new-index-create-index-title"
              >
                {t('ai.investigate.createEditIndex.create.createIndexTitle')}
              </Typography>
              <Box className="drawer-buttons-box">
                <ArrButton
                  aria-label={`${t('common.cancel')}. ${t(
                    'ai.investigate.createEditIndex.create.cancelPanelButtonHint'
                  )}`}
                  aria-live="polite"
                  aria-atomic="false"
                  variant="secondary"
                  size="medium"
                  onClick={() =>
                    formValid ? setIsUnsavedModalOpen(true) : onClose()
                  }
                  disabled={false}
                  dataTestId="create-new-index-cancel-button"
                  className="cancel-button"
                >
                  {t('common.cancel')}
                </ArrButton>

                <ArrButton
                  aria-label={`${t('common.submit')}. ${t(
                    'ai.investigate.createEditIndex.create.submitPanelButtonHint'
                  )}`}
                  aria-live="polite"
                  aria-atomic="false"
                  aria-disabled={!formValid || submitting || !Boolean(docCount)}
                  variant="primary"
                  size="medium"
                  disabled={!formValid || submitting || !Boolean(docCount)}
                  onClick={handleSubmit}
                  dataTestId="create-new-index-submit-button"
                  className="submit-button"
                >
                  {t('common.submit')}
                </ArrButton>
              </Box>
            </Box>
          </Box>

          <Box className="drawer-body" component="main">
            <Box
              className="form-section"
              component="section"
              aria-labelledby="create-index-name-heading"
            >
              <ArrTextField
                className="control"
                id="index-name"
                dataTestId="create-index-name"
                state={indexNameErr ? 'error' : 'enabled'}
                aria-label={t(
                  'ai.investigate.createEditIndex.create.inputPlaceholder'
                )}
                placeholder={t(
                  'ai.investigate.createEditIndex.create.inputPlaceholder'
                )}
                supportText={
                  indexNameErr ??
                  `${t('ai.investigate.createEditIndex.create.inputSupportText')}: ${MAX_INDEX_NAME}`
                }
                sizeVariant="large"
                value={indexName}
                onChange={(e: any) => {
                  setIndexName(e.target.value);
                }}
                onBlur={() => setTouched((p) => ({...p, indexName: true}))}
              />
            </Box>

            <Box className="form-section">
              <Box className="label-row">
                <Typography
                  component="span"
                  aria-label={t('ai.summarize.createJob.selectDocuments')}
                  className="typo-text subhead3_size"
                  data-testid="create-new-index-select-documents"
                >
                  {t('ai.summarize.createJob.selectDocuments')}
                </Typography>
                <ArrTooltipWrapper
                  data-testid="select-document-tooltip-wrapper"
                  trigger={
                    <IconButton
                      type="button"
                      size="small"
                      className="select-documents-info-button"
                      aria-label={t(
                        'ai.investigate.createEditIndex.create.selectDocumentsInfoAria'
                      )}
                      data-testid="info-icon"
                    >
                      <InfoIcon
                        className="info-icon"
                        aria-hidden
                        focusable={false}
                      />
                    </IconButton>
                  }
                >
                  <Trans
                    i18nKey="ai.investigate.createEditIndex.create.selectDocumentsTooltipContent"
                    components={{
                      br: <br data-testid="create-new-index-br" />,
                      strong: <strong data-testid="create-new-index-strong" />,
                      p: <p data-testid="create-new-index-p" />,
                    }}
                  />
                </ArrTooltipWrapper>
              </Box>

              <ArrSingleSelectDropdown
                className="control dropdown-way"
                key="dataset-dropdown"
                dataTestId="asd-index-dropdown"
                dropdownLabel={t(
                  'ai.investigate.createEditIndex.create.dropDownLabel'
                )}
                dropdownItems={dropdownItems}
                dropdownItemKey="id"
                dropdownItemDisplay="label"
                selectedValues={selectedDataset}
                onSelectionChange={handleDatasetChange}
                disabled={submitting}
                sizeVariant="medium"
                selectRestProps={{
                  'aria-label': `${t(
                    'ai.investigate.createEditIndex.create.dropDownLabel'
                  )}. ${t(
                    'ai.investigate.createEditIndex.create.selectDocumentsHint'
                  )}`,
                  'aria-haspopup': 'listbox',
                }}
              />
            </Box>

            <Box className="counts-row counts-stacked">
              <Box className="select-source-box">
                <Typography
                  component="span"
                  aria-label={t(
                    'ai.investigate.createEditIndex.create.selectedSource'
                  )}
                  className="typo-text label2_size "
                  data-testid="create-new-index-selected-source-typo"
                >
                  <span
                    className="typo-text body3_size selected-source-heading"
                    data-testid="create-new-index-selected-source"
                  >
                    {t('ai.investigate.createEditIndex.create.selectedSource')}:
                  </span>
                  {selectedLabelsDisplay}
                </Typography>
              </Box>

              <Box className="doc-count-box">
                <Typography
                  component="span"
                  aria-label={t(
                    'ai.investigate.createEditIndex.create.docCount'
                  )}
                  className="typo-text body3_size doc-count-heading"
                  data-testid="create-new-index-doc-count"
                >
                  {t('ai.investigate.createEditIndex.create.docCount')}:{' '}
                </Typography>
                {!isApiDocumentCountPending && (
                  <Typography
                    component="span"
                    aria-label={displayDocCount}
                    className="typo-text label2_size"
                    data-testid="create-new-index-total-count-text"
                  >
                    {displayDocCount}
                  </Typography>
                )}
                {isApiDocumentCountPending && (
                  <Box className="doc-count-spinner">
                    <ArrLoadingSpinner
                      show={isApiDocumentCountPending}
                      size={18}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            <Box className="form-section search-bar-control">
              <ArrTextField
                className="control search-control"
                fullWidth
                sizeVariant="medium"
                aria-label={t(
                  'ai.investigate.createEditIndex.create.searchPlaceholder'
                )}
                dataTestId="create-index-search-input"
                state={canUseTree ? 'enabled' : 'inactive'}
                placeholder={t(
                  'ai.investigate.createEditIndex.create.searchPlaceholder'
                )}
                value={searchQuery}
                onChange={(e: any) => setSearchQuery(e.target.value)}
                onKeyDown={(e: any) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (canUseTree) setAppliedQuery(searchQuery.trim());
                  }
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          aria-label="Search"
                          size="small"
                          onClick={() =>
                            canUseTree && setAppliedQuery(searchQuery.trim())
                          }
                          className="search-icon-btn"
                          disabled={!canUseTree}
                          data-testid="search-button"
                        >
                          <Search
                            aria-hidden="true"
                            data-testid="search-icon"
                            className={`${canUseTree ? 'arr-textfield-icon-color' : 'icon-disabled'}`}
                            fontSize="small"
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            <Box
              className={`tree-container ${!canUseTree ? 'is-disabled' : ''}`}
              {...(submitting && canUseTree ? {inert: true} : {})}
            >
              {!canUseTree && (
                <div
                  className="typo-text body3_size empty"
                  data-testid="create-new-index-cant-use-tree-wrapper"
                ></div>
              )}
              {canUseTree && isTreeLoading && (
                <ArrLoadingSpinner show={isTreeLoading} />
              )}
              {canUseTree && !isTreeLoading && treeItems.length === 0 && (
                <div
                  className="empty"
                  data-testid="create-new-index-no-search-results"
                >
                  {t('ai.investigate.createEditIndex.create.noSearchResults')}
                </div>
              )}
              {canUseTree && treeItems.length > 0 && (
                <ArrTreeView
                  key={`tree-${selectedDataset?.value}-${appliedQuery}`}
                  setSelectedItem={handleTreeSelected}
                  itemsList={treeItems as unknown as any[]}
                  selectedItem={treeSelectedIds[0]}
                  treeSelectedItems={treeSelectedIds}
                  allowGlobalSelect={true}
                  multipleSelection={true}
                  isShowCountLabel={false}
                  isShowCheckbox={true}
                  isChildrenIndendation={true}
                  data-testid="source-tree-structure"
                />
              )}

              {touched.tree && docsErr && (
                <div
                  className="field-error"
                  data-testid="create-new-index-docs-error"
                >
                  {docsErr}
                </div>
              )}
            </Box>
            <Box className="email-block">
              <Box className="email-head">
                <Typography
                  component="span"
                  aria-label={t(
                    'ai.investigate.createEditIndex.create.emailHeader'
                  )}
                  className="typo-text subhead3_size"
                  data-testid="create-new-index-email-header"
                >
                  {t('ai.investigate.createEditIndex.create.emailHeader')}
                </Typography>
                <Typography
                  component="span"
                  aria-label={t(
                    'ai.investigate.createEditIndex.create.emailOptional'
                  )}
                  className="typo-text body1_size"
                  data-testid="create-new-index-email-optional"
                >
                  {' ' +
                    t('ai.investigate.createEditIndex.create.emailOptional')}
                </Typography>
              </Box>
              <Box className="email-subhead">
                <Typography
                  component="span"
                  aria-label={t(
                    'ai.investigate.createEditIndex.create.emailSubHeader'
                  )}
                  className="typo-text body2_size"
                  data-testid="create-new-index-email-sub-header"
                >
                  {t('ai.investigate.createEditIndex.create.emailSubHeader')}
                </Typography>
              </Box>
              <div
                className="email-row"
                data-testid="create-new-index-email-wrapper"
              >
                <ArrTextField
                  className="control flex-1"
                  sizeVariant="large"
                  dataTestId="create-index-email"
                  fullWidth
                  aria-label={t(
                    'ai.investigate.createEditIndex.create.emailAddress'
                  )}
                  placeholder={t(
                    'ai.investigate.createEditIndex.create.emailAddress'
                  )}
                  value={emailInput}
                  state={emailError ? 'error' : 'enabled'}
                  onChange={(e: any) => setEmailInput(e.target.value)}
                  onKeyDown={(e: any) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addEmail();
                    }
                  }}
                  supportText={emailError || ''}
                />

                <ArrButton
                  variant="text"
                  className={`add-email-button as-link btn-control no-shrink}`}
                  onClick={addEmail}
                  dataTestId="create-new-index-add-button"
                >
                  {t('common.add')}
                </ArrButton>
              </div>

              {emails.length > 0 && (
                <div
                  className="email-chips"
                  data-testid="create-new-index-email-chips-wrapper"
                >
                  {emails.map((em, index) => (
                    <ArrSelectionChip
                      key={em}
                      filterData={{id: em, label: em, isSelected: false}}
                      onClick={() =>
                        setEmails((curr) => curr.filter((x) => x !== em))
                      }
                      showCloseIcon={true}
                      dataTestId={`create-index-email-chips-${index}`}
                    />
                  ))}
                </div>
              )}
            </Box>
          </Box>
        </Box>
        {isConfirmModalOpen && (
          <ConfirmModal
            onConfirm={() => {
              createIndex({
                indexName,
                sourceIds: treeSelectedIds,
                emailIdsToNotify: emails ?? [],
                sourceTypeCode: selectedDataset?.value as DropdownItemsType,
              });
            }}
            isOpen={isConfirmModalOpen}
            onClose={() => {
              setIsConfirmModalOpen(false);
            }}
            selectedDocumentCount={displayDocCount}
            isLoading={isPending}
          />
        )}
        {isUnsavedModalOpen && (
          <ArrModal
            showModal={isUnsavedModalOpen}
            showClose={false}
            onClose={() => {
              setIsUnsavedModalOpen(false);
            }}
            title={t('common.unsavedTitle')}
            ariaLabel={t('common.unsavedTitle')}
            ariaDescription={t(
              'ai.investigate.createEditIndex.modal.modificationsDescription'
            )}
            description={t(
              'ai.investigate.createEditIndex.modal.modificationsDescription'
            )}
            dataTestId="create-new-index-modal"
            buttons={[
              {
                id: '1',
                label: t('common.cancel'),
                onClick: () => {
                  setIsUnsavedModalOpen(false);
                },
                variant: 'secondary',
                ariaLabel: `${t('common.cancel')}. ${t(
                  'ai.investigate.createEditIndex.modal.cancelUnsavedHint'
                )}`,
                dataTestId: 'cni-cancel-btn',
              },
              {
                id: '2',
                label: t('ai.summarize.createJob.confirmModal.confirm'),
                onClick: () => {
                  setIsUnsavedModalOpen(false);
                  setTimeout(() => {
                    onClose();
                  }, 100);
                },
                variant: 'primary',
                ariaLabel: `${t('ai.summarize.createJob.confirmModal.confirm')}. ${t(
                  'ai.investigate.createEditIndex.modal.saveUnsavedHint'
                )}`,
                dataTestId: 'cni-confirm-btn',
              },
            ]}
          />
        )}
      </Box>
    </ArrDrawer>
  );
};

export default CreateNewIndex;
